import React, { useState, useEffect, useCallback, useMemo } from 'react';
import supabase from '../lib/supabaseClient';
import {
  AlertCircle,
  CheckCircle,
  ArrowRight,
  RefreshCw,
  Lock,
  Unlock,
  ShieldCheck,
  Info,
  CalendarClock,
  ExternalLink,
  ChevronDown,
  Flame
} from 'lucide-react';

// Hot/Critical gets its own path through this screen, never the blanket bucket override:
// a tenant has to see WHO is in this group (name + a link to the real conversation), tick
// them individually, and clear a specific acknowledgment before any of them can move. See
// supabase/20260916_hot_lead_vetted_transfer.sql for why and what it deliberately leaves
// unchanged (transfer_leads_to_campaign's eligibility CASE and the bucket-override path for
// every other locked reason).
const HOT_BUCKET = 'locked:Hot/Critical - awaiting human contact';

// Buckets ship as counts, never as rows. Da Silva uploads ~30k leads a month, and the
// previous version of this screen pulled every lead in the campaign into the browser
// just to show two numbers. `get_transfer_buckets` returns about ten rows no matter how
// large the campaign is, and `transfer_leads_by_bucket` resolves the selection to ids
// server-side, so nothing here scales with lead count.
//
// The protection rules are NOT duplicated in this file. Both RPCs derive them from
// `get_transferable_leads`, which is the one definition. See
// supabase/20260909_transfer_buckets_rpc.sql.

export default function LeadTransfer({ campaigns, user, onCreateCampaign }) {
  const [sourceCampaign, setSourceCampaign] = useState('');
  const [targetCampaign, setTargetCampaign] = useState('');
  const [buckets, setBuckets] = useState([]);
  const [selectedBuckets, setSelectedBuckets] = useState([]);
  const [loadingBuckets, setLoadingBuckets] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [transferSuccess, setTransferSuccess] = useState('');
  const [transferError, setTransferError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Date fields are discovered from the tenant's own uploaded data, by parsing VALUES
  // rather than matching field names. If a campaign has none, `dateFields` stays empty
  // and the whole section never renders. Nothing here knows what industry this is.
  const [dateFields, setDateFields] = useState([]);
  const [dateField, setDateField] = useState('');      // '' = no filter
  const [dateKeep, setDateKeep] = useState('future');  // 'future' | 'past'

  // { moved, total } while a transfer is running in batches, null otherwise.
  const [progress, setProgress] = useState(null);

  // Hot/Critical: names fetched lazily (only once this one bucket is expanded), selection
  // is per-lead, and moving any of them needs its own acknowledgment, separate from the
  // general "this cannot be undone" copy every transfer already shows.
  const [hotLeads, setHotLeads] = useState([]);
  const [hotLeadsExpanded, setHotLeadsExpanded] = useState(false);
  const [hotLeadsLoading, setHotLeadsLoading] = useState(false);
  const [hotLeadsError, setHotLeadsError] = useState('');
  const [selectedHotLeadIds, setSelectedHotLeadIds] = useState([]);
  const [hotAckChecked, setHotAckChecked] = useState(false);

  const fetchBuckets = useCallback(async () => {
    if (!sourceCampaign) return;

    setLoadingBuckets(true);
    setTransferError('');

    try {
      const { data, error: fetchError } = await supabase
        .rpc('get_transfer_buckets', {
          source_campaign_id: sourceCampaign,
          requesting_tenant_id: user.role === 'global_admin' ? null : user.tenant_id,
          p_date_field: dateField || null,
          p_date_keep: dateKeep,
          p_keep_undated: true
        });

      if (fetchError) throw fetchError;

      const rows = data || [];
      setBuckets(rows);

      // Pre-select the two buckets that are almost always the intent of a re-target:
      // people who never answered, and people who never got a first message. Anything
      // with a recorded outcome stays unchecked so it is a deliberate choice.
      setSelectedBuckets(
        rows
          .filter(b => b.is_movable && ['no_reply', 'never_contacted'].includes(b.bucket))
          .map(b => b.bucket)
      );
    } catch (err) {
      console.error('Error loading transfer buckets:', err);
      setTransferError('Could not load this campaign. Please try again.');
      setBuckets([]);
      setSelectedBuckets([]);
    } finally {
      setLoadingBuckets(false);
    }
  }, [sourceCampaign, user.tenant_id, user.role, dateField, dateKeep]);

  // Discovered once per campaign, not on every filter change.
  const fetchDateFields = useCallback(async () => {
    if (!sourceCampaign) return;
    try {
      const { data, error: dfError } = await supabase
        .rpc('get_transfer_date_fields', {
          source_campaign_id: sourceCampaign,
          requesting_tenant_id: user.role === 'global_admin' ? null : user.tenant_id
        });
      if (dfError) throw dfError;
      setDateFields(data || []);
    } catch (err) {
      // A failure here must not block the transfer itself. Worst case the optional
      // filter is unavailable and the screen behaves as it did before.
      console.error('Could not load date fields:', err);
      setDateFields([]);
    }
  }, [sourceCampaign, user.tenant_id, user.role]);

  useEffect(() => {
    if (sourceCampaign) {
      fetchBuckets();
    } else {
      setBuckets([]);
      setSelectedBuckets([]);
    }
    // A lead id selected under the PREVIOUS source campaign must never carry over silently.
    setHotLeads([]);
    setHotLeadsExpanded(false);
    setHotLeadsError('');
    setSelectedHotLeadIds([]);
    setHotAckChecked(false);
  }, [sourceCampaign, fetchBuckets]);

  const fetchHotLeads = useCallback(async () => {
    if (!sourceCampaign) return;
    setHotLeadsLoading(true);
    setHotLeadsError('');
    try {
      const { data, error: fetchError } = await supabase
        .rpc('get_hot_transfer_leads', {
          source_campaign_id: sourceCampaign,
          requesting_tenant_id: user.role === 'global_admin' ? null : user.tenant_id
        });
      if (fetchError) throw fetchError;
      setHotLeads(data || []);
    } catch (err) {
      console.error('Error loading hot leads:', err);
      setHotLeadsError('Could not load the names for this group. Try again.');
      setHotLeads([]);
    } finally {
      setHotLeadsLoading(false);
    }
  }, [sourceCampaign, user.tenant_id, user.role]);

  const toggleHotExpanded = () => {
    const next = !hotLeadsExpanded;
    setHotLeadsExpanded(next);
    if (next && hotLeads.length === 0 && !hotLeadsLoading) {
      fetchHotLeads();
    }
  };

  const toggleHotLead = (leadId) => {
    setSelectedHotLeadIds(prev =>
      prev.includes(leadId) ? prev.filter(id => id !== leadId) : [...prev, leadId]
    );
  };

  useEffect(() => {
    // Reset the filter when the campaign changes, or a field from the previous
    // campaign could stay selected and silently narrow the new one.
    setDateField('');
    setDateKeep('future');
    setDateFields([]);
    if (sourceCampaign) fetchDateFields();
  }, [sourceCampaign, fetchDateFields]);

  const activeDateField = useMemo(
    () => dateFields.find(f => f.field_key === dateField) || null,
    [dateFields, dateField]
  );

  const movableBuckets = useMemo(() => buckets.filter(b => b.is_movable), [buckets]);
  // Hot/Critical is rendered by its own dedicated block below, never through the generic
  // locked-bucket list or its one-checkbox-per-group override.
  const lockedBuckets = useMemo(
    () => buckets.filter(b => !b.is_movable && b.bucket !== HOT_BUCKET),
    [buckets]
  );
  const hotBucket = useMemo(() => buckets.find(b => b.bucket === HOT_BUCKET) || null, [buckets]);

  const selectedCount = useMemo(
    () =>
      buckets
        .filter(b => selectedBuckets.includes(b.bucket))
        .reduce((sum, b) => sum + Number(b.lead_count || 0), 0) +
      selectedHotLeadIds.length,
    [buckets, selectedBuckets, selectedHotLeadIds]
  );

  // Overridden groups are counted and surfaced separately, so the confirm step can name
  // them explicitly rather than folding them into one total the user skims past. Hot leads
  // get their OWN block in the confirm modal (with the acknowledgment), so this bucket-only
  // figure is what that block's bulleted list actually matches.
  const overriddenBuckets = useMemo(
    () => lockedBuckets.filter(b => selectedBuckets.includes(b.bucket)),
    [lockedBuckets, selectedBuckets]
  );

  const bucketOverriddenCount = useMemo(
    () => overriddenBuckets.reduce((sum, b) => sum + Number(b.lead_count || 0), 0),
    [overriddenBuckets]
  );

  // Combined figure for the plain-number summary lines (Ready-to-move card); the modal's
  // itemized list below uses bucketOverriddenCount + the dedicated Hot block instead.
  const overriddenCount = useMemo(
    () => bucketOverriddenCount + selectedHotLeadIds.length,
    [bucketOverriddenCount, selectedHotLeadIds]
  );

  // The generic "Protected" panel below only ever shows non-Hot locked groups now (Hot has
  // its own card), so its own displayed count excludes Hot leftovers.
  const otherProtectedCount = useMemo(
    () =>
      lockedBuckets
        .filter(b => !selectedBuckets.includes(b.bucket))
        .reduce((sum, b) => sum + Number(b.lead_count || 0), 0),
    [lockedBuckets, selectedBuckets]
  );

  const protectedCount = useMemo(() => {
    const hotProtected = hotBucket
      ? Math.max(Number(hotBucket.lead_count || 0) - selectedHotLeadIds.length, 0)
      : 0;
    return otherProtectedCount + hotProtected;
  }, [otherProtectedCount, hotBucket, selectedHotLeadIds]);

  const toggleBucket = (bucket) => {
    setSelectedBuckets(prev =>
      prev.includes(bucket) ? prev.filter(b => b !== bucket) : [...prev, bucket]
    );
  };

  const handleTransfer = useCallback(async () => {
    if (!sourceCampaign || !targetCampaign) {
      setTransferError('Please choose both campaigns.');
      return;
    }
    if (sourceCampaign === targetCampaign) {
      setTransferError('The two campaigns must be different.');
      return;
    }
    if (selectedBuckets.length === 0 && selectedHotLeadIds.length === 0) {
      setTransferError('Pick at least one group to move.');
      return;
    }
    if (selectedHotLeadIds.length > 0 && !hotAckChecked) {
      setTransferError('Check the Hot lead acknowledgment before moving.');
      return;
    }

    setTransferring(true);
    setTransferError('');
    setTransferSuccess('');

    // The move runs as a series of small batches, one RPC call each. Signed-in users get
    // an 8s statement timeout, and a transfer costs roughly 50ms per lead in triggers alone,
    // so one call for a whole campaign cannot fit: on 2026-09-10 Exodus hit that timeout 11
    // times in a row trying to move one. The server also caps every call's batch size, so
    // the number here is a preference, never the protection. A batch that times out rolls
    // back as a whole, which is what makes shrinking and retrying safe. Leads that moved
    // leave the source campaign, so running it again simply picks up where it stopped.
    const batchId = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
          const r = (Math.random() * 16) | 0;
          return (ch === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
        });

    let batchSize = 60;
    let moved = 0;
    let remaining = null;
    let stalls = 0;
    let consecutiveTimeouts = 0;
    setProgress({ moved: 0, total: selectedCount });

    const targetName = campaigns.find(c => c.id === targetCampaign)?.name || 'the new campaign';

    let hotMoved = 0;
    let hotSkipped = 0;

    try {
      if (selectedBuckets.length > 0) {
        for (let i = 0; i < 1000; i++) {
          const { data, error: rpcError } = await supabase
            .rpc('transfer_leads_by_bucket', {
              source_campaign_id: sourceCampaign,
              target_campaign_id: targetCampaign,
              requesting_tenant_id: user.tenant_id,
              buckets: selectedBuckets,
              p_date_field: dateField || null,
              p_date_keep: dateKeep,
              p_keep_undated: true,
              p_limit: batchSize,
              p_batch_id: batchId
            });

          if (rpcError) {
            const isTimeout = rpcError.code === '57014' || /statement timeout/i.test(rpcError.message || '');
            if (isTimeout && batchSize > 10 && consecutiveTimeouts < 4) {
              consecutiveTimeouts += 1;
              batchSize = Math.max(10, Math.floor(batchSize / 2));
              continue;
            }
            throw rpcError;
          }
          consecutiveTimeouts = 0;

          const row = data?.[0] || {};
          const thisBatch = Number(row.transferred_count || 0);
          remaining = Number(row.remaining ?? 0);
          moved += thisBatch;
          setProgress({ moved, total: Math.max(selectedCount, moved + remaining) });

          if (remaining === 0) break;

          // Guard against spinning: if two batches in a row move nothing, whatever is left
          // cannot be moved right now (it changed state mid-transfer), so stop and say so.
          if (thisBatch === 0) {
            stalls += 1;
            if (stalls >= 2) break;
          } else {
            stalls = 0;
          }
        }
      }

      // Hot/Critical leads never go through the batch loop above: they were vetted one by
      // one, so there's no volume here to page through, and this path re-checks each lead is
      // still actually Hot at the moment of the call rather than trusting the browser's list.
      if (selectedHotLeadIds.length > 0) {
        const { data: hotData, error: hotError } = await supabase
          .rpc('transfer_hot_leads_vetted', {
            p_lead_ids: selectedHotLeadIds,
            p_source_campaign_id: sourceCampaign,
            p_target_campaign_id: targetCampaign,
            p_requesting_tenant_id: user.tenant_id
          });
        if (hotError) throw hotError;
        const hotRow = hotData?.[0] || {};
        hotMoved = Number(hotRow.transferred_count || 0);
        hotSkipped = Number(hotRow.skipped_count || 0);
        moved += hotMoved;
        setProgress({ moved, total: Math.max(selectedCount, moved) });
      }

      const hotNote = hotMoved > 0
        ? ` Including ${hotMoved.toLocaleString()} Hot lead${hotMoved !== 1 ? 's' : ''} you vetted individually.`
        : '';
      const hotSkippedNote = hotSkipped > 0
        ? ` ${hotSkipped.toLocaleString()} of the Hot leads you picked could not be moved because they changed ` +
          `(likely already handled) before the move ran.`
        : '';

      if (remaining && remaining > 0) {
        setTransferSuccess(
          `Moved ${moved.toLocaleString()} lead${moved !== 1 ? 's' : ''} into ${targetName}.${hotNote} ` +
          `${remaining.toLocaleString()} could not be moved because they changed while the transfer ` +
          `was running. Refresh to see where they are now.${hotSkippedNote}`
        );
      } else {
        setTransferSuccess(
          `Moved ${moved.toLocaleString()} lead${moved !== 1 ? 's' : ''} into ${targetName}.${hotNote} ` +
          `They will be messaged from the start once that campaign is switched on.${hotSkippedNote}`
        );
      }

      setTargetCampaign('');
      setTimeout(() => setTransferSuccess(''), 10000);
    } catch (err) {
      console.error('Error transferring leads:', err);
      setTransferError(
        moved > 0
          ? `Moved ${moved.toLocaleString()} lead${moved !== 1 ? 's' : ''} into ${targetName} before it stopped ` +
            `(${err.message}). Nothing was lost. Run the transfer again and it will carry on with the rest.`
          : `Transfer failed: ${err.message}`
      );
    } finally {
      setTransferring(false);
      setProgress(null);
      setShowConfirmModal(false);
      setSelectedHotLeadIds([]);
      setHotAckChecked(false);
      setHotLeadsExpanded(false);
      setHotLeads([]);
      // Refresh either way: after a partial run the counts on screen are stale.
      await fetchBuckets();
    }
  }, [sourceCampaign, targetCampaign, selectedBuckets, selectedHotLeadIds, hotAckChecked, selectedCount, user.tenant_id, campaigns, fetchBuckets, dateField, dateKeep]);

  const activeCampaigns = campaigns.filter(c => !c.archived);
  const sourceName = campaigns.find(c => c.id === sourceCampaign)?.name;
  const targetName = campaigns.find(c => c.id === targetCampaign)?.name;

  return (
    <div className="space-y-6">
      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900">Re-target leads with a new campaign</h3>
            <p className="text-sm text-blue-800 mt-1">
              Choose which groups of leads to move. Anyone who told you no, opted out, or is
              mid-conversation is protected and stays where they are. Moved leads start over
              from the first message of the new campaign.
            </p>
            <p className="text-sm text-blue-800 mt-2">
              Take a moment over the groups. We only know what happened inside SurFox, so
              anything that changed outside a conversation with us will not show up here.
            </p>
          </div>
        </div>
      </div>

      {/* Success */}
      {transferSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <span className="text-green-800 font-medium">{transferSuccess}</span>
          </div>
        </div>
      )}

      {/* Error */}
      {transferError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-800 font-medium">{transferError}</span>
          </div>
        </div>
      )}

      {/* Campaign Selection */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Campaigns</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              From Campaign
            </label>
            <select
              value={sourceCampaign}
              onChange={(e) => setSourceCampaign(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="">Select source campaign...</option>
              {activeCampaigns.map(campaign => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-center pb-2">
            <ArrowRight className="w-6 h-6 text-gray-400" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              To Campaign
            </label>
            <select
              value={targetCampaign}
              onChange={(e) => {
                if (e.target.value === 'CREATE_NEW') {
                  onCreateCampaign();
                  setTargetCampaign('');
                } else {
                  setTargetCampaign(e.target.value);
                }
              }}
              disabled={!sourceCampaign}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">Select target campaign...</option>
              <option value="CREATE_NEW" className="font-semibold text-blue-600">
                ➕ Create New Campaign
              </option>
              <option disabled>──────────</option>
              {activeCampaigns
                .filter(c => c.id !== sourceCampaign)
                .map(campaign => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              {activeCampaigns.filter(c => c.id !== sourceCampaign).length === 0 && (
                <option disabled>No campaigns available</option>
              )}
            </select>
          </div>
        </div>
      </div>

      {/* Date filter. Renders ONLY when this campaign's own data contains something
          that parses as a date, so most tenants never see it at all. */}
      {sourceCampaign && dateFields.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-start gap-3">
            <CalendarClock className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900">
                We found a date on these leads
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Your list includes {dateFields.length === 1 ? 'a date field' : 'date fields'} we
                can filter on. Use it if some of these leads have gone past their date and should
                be left alone. Optional, and off by default.
              </p>

              <div className="flex flex-wrap items-center gap-3 mt-4">
                <select
                  value={dateField}
                  onChange={(e) => setDateField(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">No date filter</option>
                  {dateFields.map(f => (
                    <option key={f.field_key} value={f.field_key}>
                      {f.field_key} ({Number(f.leads_with_date).toLocaleString()} dated)
                    </option>
                  ))}
                </select>

                {dateField && (
                  <>
                    <span className="text-sm text-gray-600">only move leads whose date is</span>
                    <select
                      value={dateKeep}
                      onChange={(e) => setDateKeep(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="future">still ahead</option>
                      <option value="past">already passed</option>
                    </select>
                  </>
                )}
              </div>

              {activeDateField && (
                <div className="mt-3 bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                  <p className="text-sm text-indigo-900">
                    Excluding{' '}
                    <span className="font-semibold">
                      {Number(
                        dateKeep === 'future'
                          ? activeDateField.leads_in_past
                          : activeDateField.leads_in_future
                      ).toLocaleString()}
                    </span>{' '}
                    lead{
                      Number(dateKeep === 'future' ? activeDateField.leads_in_past : activeDateField.leads_in_future) !== 1
                        ? 's' : ''
                    }{' '}
                    whose {activeDateField.field_key} is{' '}
                    {dateKeep === 'future' ? 'already passed' : 'still ahead'}. The counts below
                    already reflect this.
                  </p>
                  {activeDateField.parse_rate < 100 && (
                    <p className="text-xs text-indigo-800 mt-1">
                      Around {100 - activeDateField.parse_rate}% of the values in this field were
                      not readable as a date. Those leads are kept rather than dropped, so check
                      them if the number looks off.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bucket picker */}
      {sourceCampaign && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-lg font-semibold text-gray-900">Who do you want to move?</h3>
            <button
              onClick={fetchBuckets}
              disabled={loadingBuckets}
              className="text-blue-600 hover:text-blue-700 flex items-center space-x-1"
            >
              <RefreshCw className={`w-4 h-4 ${loadingBuckets ? 'animate-spin' : ''}`} />
              <span className="text-sm">Refresh</span>
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Tick the groups you want in {targetName ? <span className="font-medium">{targetName}</span> : 'the new campaign'}.
            Everyone in a ticked group gets a fresh first message, so leave a group unticked
            if you are not sure about it.
          </p>

          {loadingBuckets ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Counting leads...</p>
            </div>
          ) : buckets.length === 0 ? (
            // No buckets at all means no leads at all. Distinct from "all protected",
            // which returns locked buckets and renders the panel below.
            <div className="text-center py-8 text-gray-600">
              <p className="font-medium text-gray-900">This campaign has no leads in it.</p>
              <p className="text-sm mt-1">
                Pick a different campaign to move leads out of.
              </p>
            </div>
          ) : movableBuckets.length === 0 ? (
            <div className="text-center py-8 text-gray-600">
              <p className="font-medium text-gray-900">Nothing here can be moved.</p>
              <p className="text-sm mt-1">
                Every lead in this campaign is protected. See the list below for why.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {movableBuckets.map(b => {
                const checked = selectedBuckets.includes(b.bucket);
                return (
                  <label
                    key={b.bucket}
                    className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                      checked
                        ? 'border-blue-400 bg-blue-50'
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleBucket(b.bucket)}
                      className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="font-medium text-gray-900">{b.bucket_label}</span>
                        <span className={`text-lg font-bold tabular-nums ${checked ? 'text-blue-700' : 'text-gray-500'}`}>
                          {Number(b.lead_count).toLocaleString()}
                        </span>
                      </div>
                      {b.bucket_help && (
                        <p className="text-sm text-gray-600 mt-0.5">{b.bucket_help}</p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Hot/Critical: vetted individually, never a bucket-level override */}
      {sourceCampaign && !loadingBuckets && hotBucket && Number(hotBucket.lead_count) > 0 && (
        <div className="bg-white rounded-xl border border-red-200 shadow-sm p-6">
          <div className="flex items-center justify-between gap-3 mb-1">
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-red-600" />
              <h3 className="text-lg font-semibold text-gray-900">
                {hotBucket.bucket_label} ({Number(hotBucket.lead_count).toLocaleString()})
              </h3>
            </div>
            <button
              onClick={toggleHotExpanded}
              className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm flex-shrink-0"
            >
              <span>{hotLeadsExpanded ? 'Hide names' : 'Show me who'}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${hotLeadsExpanded ? 'rotate-180' : ''}`} />
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            SurFox already got these leads to the finish line and handed them to your team.
            These do not move as a group — open the list, look at who's in it, and tick only
            the ones you've checked yourself.
          </p>

          {hotLeadsExpanded && (
            hotLeadsLoading ? (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500 mx-auto"></div>
              </div>
            ) : hotLeadsError ? (
              <p className="text-sm text-red-700">{hotLeadsError}</p>
            ) : hotLeads.length === 0 ? (
              <p className="text-sm text-gray-600">
                None of these are showing up right now — they may have already been handled.
                Try Refresh above.
              </p>
            ) : (
              <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
                {hotLeads.map(l => {
                  const checked = selectedHotLeadIds.includes(l.lead_id);
                  return (
                    <div
                      key={l.lead_id}
                      className={`flex items-center gap-3 px-4 py-3 ${checked ? 'bg-amber-50' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleHotLead(l.lead_id)}
                        aria-label={`Include ${l.name || l.phone}`}
                        className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{l.name || 'Unnamed lead'}</div>
                        <div className="text-xs text-gray-500">
                          {l.phone}
                          {l.marked_hot_at && ` · Hot since ${new Date(l.marked_hot_at).toLocaleDateString()}`}
                        </div>
                      </div>
                      <a
                        href={`https://surfox.ai/lead/${l.lead_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm flex-shrink-0"
                      >
                        <span>View</span>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  );
                })}
              </div>
            )
          )}

          {selectedHotLeadIds.length > 0 && (
            <p className="text-sm text-red-700 mt-3 font-medium">
              {selectedHotLeadIds.length.toLocaleString()} of {hotLeads.length.toLocaleString()} Hot
              lead{selectedHotLeadIds.length !== 1 ? 's' : ''} selected to move.
            </p>
          )}
        </div>
      )}

      {/* Protected (excludes Hot/Critical, which has its own card above) */}
      {sourceCampaign && !loadingBuckets && lockedBuckets.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center space-x-2 mb-1">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Protected ({otherProtectedCount.toLocaleString()})
            </h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            These stay put. They cannot be moved or messaged by the new campaign.
          </p>

          <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
            {lockedBuckets.map(b => {
              const checked = selectedBuckets.includes(b.bucket);
              const overridable = b.can_override;

              return (
                <div
                  key={b.bucket}
                  className={`px-4 py-3 ${checked ? 'bg-amber-50' : ''}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      {overridable ? (
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleBucket(b.bucket)}
                          aria-label={`Move ${b.bucket_label} anyway`}
                          className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                        />
                      ) : (
                        <Lock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      )}
                      <span className={`text-sm truncate ${checked ? 'font-medium text-amber-900' : 'text-gray-700'}`}>
                        {b.bucket_label}
                      </span>
                    </div>
                    <span className={`text-sm font-semibold tabular-nums ${checked ? 'text-amber-800' : 'text-gray-900'}`}>
                      {Number(b.lead_count).toLocaleString()}
                    </span>
                  </div>
                  {b.bucket_help && (
                    <p className={`text-xs mt-1 ${overridable ? 'ml-6' : 'ml-6'} ${checked ? 'text-amber-800' : 'text-gray-500'}`}>
                      {b.bucket_help}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {lockedBuckets.some(b => b.can_override) && (
            <p className="text-sm text-gray-600 mt-3">
              You can tick a group above to move it anyway. Only do that when you know
              something we do not. Opted-out contacts can never be moved.
            </p>
          )}

          {/* Honest about the limits of the protection. It is built from what leads
              actually said to us, so it cannot cover what they never mentioned. */}
          <div className="mt-4 flex items-start gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
            <Info className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
            <p>
              This is built from what people actually told us. If someone mentioned it only
              in passing, or their situation changed somewhere we could not see it, they may
              still be sitting in one of the groups above. Worth a look before a big move.
            </p>
          </div>
        </div>
      )}

      {/* Transfer action */}
      {sourceCampaign && targetCampaign && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Ready to move</h3>
              <p className="text-sm text-gray-600 mt-1">
                From <span className="font-medium">{sourceName}</span> to{' '}
                <span className="font-medium">{targetName}</span>
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 flex items-center gap-2">
                  <Unlock className="w-4 h-4 text-green-600" />
                  Moving
                </span>
                <span className="font-semibold text-green-700 tabular-nums">
                  {selectedCount.toLocaleString()} lead{selectedCount !== 1 ? 's' : ''}
                </span>
              </div>
              {overriddenCount > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600" />
                    Of those, protection overridden
                  </span>
                  <span className="font-semibold text-amber-700 tabular-nums">
                    {overriddenCount.toLocaleString()} lead{overriddenCount !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 flex items-center gap-2">
                  <Lock className="w-4 h-4 text-gray-400" />
                  Staying behind, protected
                </span>
                <span className="font-semibold text-gray-700 tabular-nums">
                  {protectedCount.toLocaleString()} lead{protectedCount !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-end">
              <button
                onClick={() => setShowConfirmModal(true)}
                disabled={transferring || selectedCount === 0}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <ArrowRight className="w-4 h-4" />
                <span>Move {selectedCount.toLocaleString()} lead{selectedCount !== 1 ? 's' : ''}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={() => !transferring && setShowConfirmModal(false)}
            />

            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-auto p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Move {selectedCount.toLocaleString()} lead{selectedCount !== 1 ? 's' : ''} to {targetName}?
              </h3>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-amber-900 mb-2 font-medium">
                  These leads start over from scratch:
                </p>
                <ul className="text-sm text-amber-800 space-y-1 list-disc list-inside">
                  <li>They get the new campaign's first message, as if never contacted</li>
                  <li>Their score and status reset</li>
                  <li>Any scheduled follow-up is cancelled</li>
                  <li>Their past conversation is kept, but the AI starts fresh</li>
                </ul>
              </div>

              {bucketOverriddenCount > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-red-900 font-medium mb-2">
                    You are overriding protection on {bucketOverriddenCount.toLocaleString()} lead
                    {bucketOverriddenCount !== 1 ? 's' : ''}:
                  </p>
                  <ul className="text-sm text-red-800 space-y-1 list-disc list-inside">
                    {overriddenBuckets.map(b => (
                      <li key={b.bucket}>
                        {b.bucket_label} ({Number(b.lead_count).toLocaleString()})
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-red-800 mt-2">
                    These people were held back for a reason. They will be treated as brand new
                    and messaged from the start.
                  </p>
                </div>
              )}

              {selectedHotLeadIds.length > 0 && (
                <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-4">
                  <p className="text-sm text-red-900 font-semibold mb-2 flex items-center gap-2">
                    <Flame className="w-4 h-4" />
                    {selectedHotLeadIds.length.toLocaleString()} Hot lead
                    {selectedHotLeadIds.length !== 1 ? 's' : ''} selected individually
                  </p>
                  <p className="text-sm text-red-800 mb-3">
                    This lead is marked Hot -- SurFox already got them to the finish line and
                    handed them to your team, whether or not your team has replied yet. Starting
                    over throws that away and treats them as a brand-new lead. And because the
                    old conversation isn't deleted, the new campaign's AI will still be able to
                    see everything already said -- so if this lead brings up something from
                    before, the AI may respond oddly since it thinks it's starting fresh.
                  </p>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hotAckChecked}
                      onChange={(e) => setHotAckChecked(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-red-400 text-red-600 focus:ring-red-500 flex-shrink-0"
                    />
                    <span className="text-sm text-red-900 font-medium">
                      I understand this, and I'm choosing to move these specific leads anyway.
                    </span>
                  </label>
                </div>
              )}

              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-emerald-900">
                  <span className="font-medium">{protectedCount.toLocaleString()} protected lead
                  {protectedCount !== 1 ? 's' : ''}</span> are not included, and opted-out
                  contacts are never included no matter what is ticked.
                </p>
                <p className="text-sm text-emerald-800 mt-2">
                  It is worth saying this will not be perfect. We can only protect people based
                  on what they told us, so if something changed that never came up in a
                  conversation, a few may still be in this group.
                </p>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                {transferring
                  ? 'Large lists move in batches. Keep this window open until it finishes.'
                  : 'This cannot be undone.'}
              </p>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  disabled={transferring}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTransfer}
                  disabled={transferring || (selectedHotLeadIds.length > 0 && !hotAckChecked)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  {transferring ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>
                        {progress && progress.total > 0
                          ? `Moving ${progress.moved.toLocaleString()} of ${progress.total.toLocaleString()}...`
                          : 'Moving...'}
                      </span>
                    </>
                  ) : (
                    <span>Yes, move them</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
